import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import {
  CorporateAdminDto,
  CreateCorporateAdminDto,
  PagedResultDto,
  UpdateCorporateAdminDto,
  TerminateCorporateAdminDto,
  CorporateAdminsSearchDto
} from '../../models';
import { HttpHelper } from '@umss/shared/utils';

@Injectable({
  providedIn: 'root'
})
export class CorporateAdminsService {
  private readonly baseUrl = '/api/Corporate';
  private readonly http = inject(HttpClient);

  getAll(corporationId: string): Observable<CorporateAdminDto[]> {
    return this.http.get<CorporateAdminDto[]>(
      `${this.baseUrl}/${corporationId}/admins`
    );
  }

  create(
    corporationId: string,
    requestBody: CreateCorporateAdminDto
  ): Observable<CorporateAdminDto> {
    return this.http.post<CorporateAdminDto>(
      `${this.baseUrl}/${corporationId}/admins`,
      requestBody
    );
  }

  getAdminsByLocation(locationId: string): Observable<CorporateAdminDto[]> {
    return this.http.get<CorporateAdminDto[]>(
      `${this.baseUrl}/locations/${locationId}/admins`
    );
  }

  getAllSearch(
    corporationId: string,
    parameters?: CorporateAdminsSearchDto
  ): Observable<PagedResultDto<CorporateAdminDto>> {
    const params = HttpHelper.toHttpParams(parameters ?? {});
    return this.http.get<PagedResultDto<CorporateAdminDto>>(
      `${this.baseUrl}/${corporationId}/admins/search`,
      { params }
    );
  }

  get(adminId: string): Observable<CorporateAdminDto> {
    return this.http.get<CorporateAdminDto>(
      `${this.baseUrl}/admins/${adminId}`
    );
  }

  update(
    adminId: string,
    requestBody: UpdateCorporateAdminDto
  ): Observable<CorporateAdminDto> {
    return this.http.put<CorporateAdminDto>(
      `${this.baseUrl}/admins/${adminId}`,
      requestBody
    );
  }

  delete(adminId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admins/${adminId}`);
  }

  getEmail(email: string): Observable<CorporateAdminDto> {
    return this.http.get<CorporateAdminDto>(
      `${this.baseUrl}/admins/email/${email}`
    );
  }

  getUser(userId: string): Observable<CorporateAdminDto> {
    return this.http.get<CorporateAdminDto>(
      `${this.baseUrl}/admins/user/${userId}`
    );
  }

  sendInvitation(id: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/admins/${id}/send-invitation`,
      {}
    );
  }

  resetPassword(id: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/admins/${id}/reset-password`,
      {}
    );
  }

  terminate(id: string, payload: TerminateCorporateAdminDto): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/admins/${id}/terminate`,
      payload
    );
  }
}
